<template>
<w-sliding-popup ref="smartAnalyzerPopup" title="Smart Analyzer" width="800px" @close="onClose">
  <div class="tab-wrapper" slot="content">
    <w-event-hist-select-component v-show="!hiddenSelectCmp" :class="hiddenSelectCmp ? null : 'mr-b10'" :event-hist-seq-numbers="eventHistSeqNumbers" @select="setEventHistData" @reset="reset" />
    <hr v-show="!hiddenSelectCmp" :class="hiddenSelectCmp ? null : 'mr-b25'" />
    <el-row class="title-bar mr-t10" type="flex" justify="space-between">
      <div class="vertical-center-box">
        <div class="title-h4">이벤트 정보</div>
      </div>
      <w-recommend-handling-trouble-button :event-hist-seq-numbers="selectEventSeq"></w-recommend-handling-trouble-button>
    </el-row>
    <div class="mr-b25">
      <el-row class="list form-list mr-b10">
        <el-col :span="12" class="pd-r20">
          <el-form label-position="left">
            <el-form-item label="대상" label-width="120px">{{eventHistFormData.objName}}</el-form-item>
            <el-form-item label="관리구분" label-width="120px">{{eventHistFormData.managetypeName}}</el-form-item>
            <el-form-item label="장애티켓 발생여부" label-width="120px">
              {{eventHistFormData.troubleTicketData}}
              <span v-show="!isNil(eventHistFormData.troubleTicketGroupId)" class="mr-r4 link c-pointer" @click="showTroubleTicketPopup">(#{{eventHistFormData.troubleTicketGroupId}})</span>
            </el-form-item>
          </el-form>
        </el-col>
        <el-col :span="12">
          <el-form size="mini" label-position="left">
            <el-form-item label="등급" label-width="120px">{{eventHistFormData.eventlevelName}}</el-form-item>
            <el-form-item label="자원구분" label-width="120px">{{eventHistFormData.rsctypeName}}</el-form-item>
            <el-form-item label="이벤트 확인여부" label-width="120px">{{eventHistFormData.eventConfirmData}}</el-form-item>
          </el-form>
        </el-col>
      </el-row>
      <el-input readonly resize="none" type="textarea" v-model="eventHistFormData.eventMessage" :rows="3" class="ticket-input-class"></el-input>
    </div>
    <el-tabs v-model="activeTab">
      <el-tab-pane label="장애근원지 분석" name="rca">
        <w-root-cause-analysis-view :event-hist-data="eventHistData" />
      </el-tab-pane>
      <el-tab-pane label="과거 분석" name="past">
        <w-event-past-analysis-view :event-hist-data="eventHistData" />
      </el-tab-pane>
      <el-tab-pane ref="futureTab" label="미래 예측" name="future">
        <w-event-future-predict-view :event-hist-data="eventHistData" />
      </el-tab-pane>
      <el-tab-pane v-if="isNumberTypeEvent" label="성능 예측" name="performance">
        <w-performance-predict-component :rsc-info="eventHistData" />
      </el-tab-pane>
    </el-tabs>
    <w-event-ticket-status-popup ref="ticketPopup" :trouble-ticket-info="troubleTicketInfo"></w-event-ticket-status-popup>
  </div>
</w-sliding-popup>
</template>

<script>
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { getValueAfterCheckEmpty, EMPTY_TEXT } from '@/utils/string';
import { getDateToString } from '@/utils/date';
import WEventTicketStatusPopup from '@/views/event/components/popup/EventTicketStatusPopup';
import WEventSmartAnalyzerPopup from '@/components/popup/EventSmartAnalyzerPopup';
import WRecommendHandlingTroubleButton from '@/views/event/components/RecommendHandlingTroubleButton';
import WPerformancePredictComponent from '@/components/popup/ResourceSmartAnalyzerPopup/components/PerformancePredictComponent';
import WEventHistSelectComponent from './components/EventHistSelectComponent';
import WRootCauseAnalysisView from './RootCauseAnalysisView';
import WEventPastAnalysisView from './EventPastAnalysisView';
import WEventFuturePredictView from './EventFuturePredictView';

export default {
  name: 'WEventSmartAnalyzerPopup',
  components: {
    WEventHistSelectComponent,
    WRootCauseAnalysisView,
    WEventPastAnalysisView,
    WEventFuturePredictView,
    WEventTicketStatusPopup,
    WEventSmartAnalyzerPopup,
    WPerformancePredictComponent,
    WRecommendHandlingTroubleButton,
  },
  data() {
    return {
      activeTab: 'rca',
      eventHistData: {},
      eventHistFormData: {},
      troubleTicketInfo: {},
      selectEventSeq: [],
    };
  },
  computed: {
    visible() {
      return this.$store.state.smartAnalyzer.showEventPopup;
    },
    eventHistSeqNumbers() {
      return this.$store.state.smartAnalyzer.eventHistSeqNumbers;
    },
    /**
     * 이벤트 선택 컴포넌트 숨기기 여부
     * @author Ju
     * @return {Boolean} true: 숨김 / false: 제공
     */
    hiddenSelectCmp() {
      return isEmpty(this.eventHistSeqNumbers) || this.eventHistSeqNumbers.length === 1;
    },
    isNumberTypeEvent() {
      return !isNil(this.eventHistData) && this.eventHistData.managetypeId !== 'SVC' && (this.eventHistData.rscDatatypeId === 'INTEGER' || this.eventHistData.rscDatatypeId === 'DOUBLE');
    },
  },
  watch: {
    visible: {
      handler(value) {
        if (value) {
          this.open();
        } else {
          this.close();
        }
      },
    },
    eventHistSeqNumbers: {
      handler(value) {
        if (isEmpty(value)) {
          this.reset();
        }
      },
    },
    eventHistData: {
      handler(value) {
        if (isEmpty(value)) {
          this.eventHistFormData = {};
        } else {
          const eventConfirmData = isNil(value.confirmMaangerId) ? '미확인' : `${value.confirmManagerId} (${this.getDateToString(value.confirmDatetime)})`;
          const troubleTicketData = isNil(value.troubleTicketGroupId) ? '미처리' : value.troubleStatusName;
          this.eventHistFormData = {
            ...value,
            eventConfirmData,
            troubleTicketData,
          };
        }
      },
    },
    isNumberTypeEvent: {
      handler(to) {
        if (!to && this.activeTab === 'performance') {
          this.activeTab = 'rca';
        }
      },
    },
  },
  beforeDestroy() {
    if (this.visible) {
      this.close();
    }
  },
  methods: {
    isNil,
    getValueAfterCheckEmpty,
    /**
     * 팝업을 연다.
     * @author Ju
     */
    open() {
      this.$refs.smartAnalyzerPopup.open();
      /* TODO IE 이슈 대응 */
      this.$nextTick(() => {
        const futureTab = this.$refs.futureTab.$el;
        if (futureTab.style.cssText.indexOf('display: block !important;') >= 0) {
          futureTab.style.cssText = futureTab.style.cssText.replace('display: block !important;', 'display: none;');
        }
        /* IE 이슈 대응 */
      });
    },
    /**
     * 팝업을 닫는다.
     * @author Ju
     */
    close() {
      this.$refs.smartAnalyzerPopup.close();
    },
    /**
     * 팝업 close 이벤트 핸들러
     * @author Ju
     */
    onClose() {
      this.eventHistList = [];
      this.activeTab = 'rca';
      this.$store.dispatch('closeEventSmartAnalyzerPopup');
    },
    /**
     * 화면을 초기화한다.
     * @author Ju
     */
    reset() {
      this.eventHistData = {};
    },
    setEventHistData(eventHistData) {
      // 선택된 이벤트에 대한 장애조치 추천을 하기 위함
      this.selectEventSeq = [eventHistData.seqNumber];
      this.eventHistData = eventHistData;
    },
    /**
     * 이벤트의 장애 티켓 상세 팝업을 연다.
     * @author Ju
     */
    showTroubleTicketPopup() {
      this.troubleTicketInfo = {
        troubleTicketGroupId: this.eventHistData.troubleTicketGroupId,
      };
      this.$refs.ticketPopup.open();
    },
    /**
     * 날짜 데이터를 문자열 형태로 변환한다.
     * @author Ju
     * @param  {Date} value
     */
    getDateToString(value) {
      if (!isNil(value)) {
        return getDateToString(value);
      }
      return EMPTY_TEXT;
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/components/popup/EventSmartAnalyzerPopup/index.vue